import { Link } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { Reveal } from "@/components/motion/Reveal";
import { CTASection } from "@/components/CTASection";
import { BrandMarquee } from "@/components/Marquee";
import { BUSINESS, CHAPTERS, WHY_US } from "@/data/content";

const values = [
  "We adapt to your needs, not a rigid template.",
  "We care about your whole business, not just a webpage.",
  "We build modern, mobile-first, fast experiences.",
  "We're local and personable — you always reach a real person.",
];

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHeader
        overline="About · Est. 2026"
        titleLines={["Websites", "built to win."]}
        subtitle={BUSINESS.mission}
      />

      <section className="bg-white py-24 sm:py-32">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-brand">Our story</p>
            <h2 className="mt-6 font-display text-4xl font-extrabold leading-[0.98] tracking-tight sm:text-5xl">
              A local studio with a bigger mission.
            </h2>
            <div className="mt-6 space-y-5 text-lg leading-relaxed text-ink/65">
              <p>
                {BUSINESS.name} was founded by {BUSINESS.owner} on a simple belief: local
                businesses deserve websites just as good as the big brands they admire —
                companies like Apple and Google that are clean, fast, and effortless to use.
              </p>
              <p>
                Too many great businesses are held back by outdated websites, no website at
                all, or a page that quietly loses customers. We're here to change that.
              </p>
              <p>
                But we don't stop at design. We want to help your company grow in every
                aspect — with automation, strategy, and a partner who's genuinely invested
                in your success.
              </p>
            </div>
            <Link
              to="/contact"
              data-testid="about-cta"
              className="mt-9 inline-flex items-center gap-2 rounded-full bg-brand px-7 py-4 font-bold text-ink transition-transform hover:-translate-y-0.5 hover:bg-brand-dark hover:text-white"
            >
              Work with us <ArrowUpRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      <BrandMarquee dark />

      <section className="bg-paper py-24 sm:py-32">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-brand">The way we work</p>
            <h2 className="mt-6 max-w-3xl font-display text-4xl font-extrabold leading-[0.95] tracking-tight sm:text-6xl">
              We don't just build websites. We build growth.
            </h2>
          </Reveal>
          <div className="mt-16 grid gap-px overflow-hidden rounded-2xl border border-black/10 bg-black/10 md:grid-cols-3">
            {CHAPTERS.map((c, i) => (
              <Reveal key={c.no} delay={i * 0.08} className="bg-white">
                <div className="flex h-full flex-col p-8 sm:p-10">
                  <span className="font-mono text-sm font-bold text-brand">{c.no} /</span>
                  <h3 className="mt-4 font-display text-2xl font-bold tracking-tight">{c.title}</h3>
                  <p className="mt-4 text-[15px] leading-relaxed text-ink/60">{c.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-24 sm:py-32">
        <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-14 px-5 sm:px-8 lg:grid-cols-2">
          <Reveal>
            <h2 className="font-display text-4xl font-extrabold leading-[0.98] tracking-tight sm:text-5xl">
              What we stand for.
            </h2>
            <ul className="mt-8 space-y-5">
              {values.map((v) => (
                <li key={v} className="flex items-start gap-4">
                  <span className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full bg-brand text-white">
                    <Check className="h-3.5 w-3.5" />
                  </span>
                  <span className="text-lg text-ink/70">{v}</span>
                </li>
              ))}
            </ul>
          </Reveal>
          <div className="grid gap-px overflow-hidden rounded-2xl border border-black/10 bg-black/10 sm:grid-cols-2">
            {WHY_US.map((w, i) => (
              <Reveal key={w.title} delay={i * 0.06} className="bg-white">
                <div className="h-full p-7">
                  <w.icon className="h-6 w-6 text-brand" />
                  <h3 className="mt-4 font-display text-lg font-bold tracking-tight">{w.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-ink/60">{w.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
